import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Settings Context
 *
 * React Context for managing plugin settings with optimistic updates,
 * loading states, and error handling
 *
 * @package WooAIChatbot
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
/**
 * Transform PHP flat settings to frontend nested structure
 */
const transformFromPhp = (raw) => {
    return {
        aiProvider: {
            provider: raw.ai_provider || 'gemini',
            geminiApiKey: raw.gemini_api_key || '',
            openaiApiKey: raw.openai_api_key || '',
            claudeApiKey: raw.claude_api_key || '',
            geminiModel: raw.model || 'gemini-2.5-flash',
            openaiModel: raw.openai_model || 'gpt-4.1-mini',
            claudeModel: raw.claude_model || 'claude-sonnet-4-5-20251101',
            temperature: raw.temperature ?? 0.7,
            maxTokens: raw.max_tokens ?? 2048,
            enableFallback: raw.enable_fallback ?? false,
            fallbackProvider: raw.fallback_provider || 'openai',
            enableStreaming: raw.enable_streaming ?? true,
            llmRules: Array.isArray(raw.llm_rules) ? raw.llm_rules : [],
            thinkingEnabled: raw.thinking_enabled ?? false,
            thinkingBudget: raw.thinking_budget ?? 512,
        },
        general: {
            sessionTimeout: 30,
            rateLimitPerMinute: 60,
            cacheTtl: 3600,
            debugMode: false,
            logLevel: 'info',
        },
        appearance: {
            widgetPosition: raw.widget_position || 'bottom-right',
            primaryColor: raw.widget_color || '#6366F1',
            secondaryColor: raw.secondary_color || '#4F46E5',
            accentColor: raw.accent_color || '#10B981',
            textColor: raw.text_color || '#111827',
            widgetBackground: raw.widget_background || '#FFFFFF',
            cardBackground: raw.card_background || '#FFFFFF',
            cardTextColor: raw.card_text_color || '#111827',
            fontFamily: raw.font_family || 'Inter (Default)',
            fontSizeScale: raw.font_size_scale || 'Medium (Default)',
            widgetSize: raw.widget_size || 'medium',
            showGreeting: raw.show_greeting ?? true,
            greetingMessage: raw.welcome_message || 'Welcome! How can I help you today?',
            widgetWidth: raw.widget_width || 380,
            maxHeight: raw.max_height || 600,
            botAvatar: raw.bot_avatar || '🤖',
            botName: raw.bot_name || 'AI Assistant',
            showRating: raw.show_rating ?? true,
            showViewProductButton: raw.show_view_product_button ?? true,
            cardBorderRadius: raw.card_border_radius ?? 16,
            cardShadow: raw.card_shadow || 'medium',
            customCSS: raw.custom_css || '',
            launcherStyle: raw.launcher_style || 'pill',
            sidebarEnabled: raw.sidebar_enabled ?? true,
            sidebarSide: raw.sidebar_side || 'right',
            sidebarWidth: raw.sidebar_width ?? 30,
        },
        languages: {
            default: raw.language || 'en',
            enabled: ['en'],
            autoDetect: true,
            welcomeMessages: raw.welcome_messages,
        },
        // RAG settings - pass through directly (already nested in PHP)
        rag: raw.rag ? {
            enabled: raw.rag.enabled ?? false,
            embeddingModel: raw.rag.embeddingModel || 'gemini-embedding-001',
            chunkSize: raw.rag.chunkSize ?? 500,
            chunkOverlap: raw.rag.chunkOverlap ?? 100,
            topK: raw.rag.topK ?? 5,
            similarityThreshold: raw.rag.similarityThreshold ?? 0.7,
            supabaseUrl: raw.rag.supabaseUrl || '',
            supabaseApiKey: raw.rag.supabaseApiKey || '',
            supabaseTableName: raw.rag.supabaseTableName || 'product_embeddings',
            supabaseConnectionString: raw.rag.supabaseConnectionString || '',
            indexPages: raw.rag.indexPages ?? false,
            indexPosts: raw.rag.indexPosts ?? false,
        } : undefined,
    };
};
/**
 * Transform frontend nested structure to PHP flat settings
 */
const transformToPhp = (settings) => {
    const result = {};
    // AI Provider settings
    if (settings.aiProvider) {
        result.ai_provider = settings.aiProvider.provider;
        result.model = settings.aiProvider.geminiModel || settings.aiProvider.openaiModel || settings.aiProvider.claudeModel;
        result.openai_model = settings.aiProvider.openaiModel;
        result.claude_model = settings.aiProvider.claudeModel;
        result.gemini_api_key = settings.aiProvider.geminiApiKey;
        result.openai_api_key = settings.aiProvider.openaiApiKey;
        result.claude_api_key = settings.aiProvider.claudeApiKey;
        result.enable_fallback = settings.aiProvider.enableFallback;
        result.fallback_provider = settings.aiProvider.fallbackProvider;
        // Advanced settings
        result.temperature = settings.aiProvider.temperature;
        result.max_tokens = settings.aiProvider.maxTokens;
        result.enable_streaming = settings.aiProvider.enableStreaming;
        if (settings.aiProvider.llmRules) {
            result.llm_rules = settings.aiProvider.llmRules;
        }
        result.thinking_enabled = settings.aiProvider.thinkingEnabled;
        result.thinking_budget = settings.aiProvider.thinkingBudget;
    }
    // Appearance settings
    if (settings.appearance) {
        result.widget_position = settings.appearance.widgetPosition;
        result.widget_color = settings.appearance.primaryColor;
        result.secondary_color = settings.appearance.secondaryColor;
        result.accent_color = settings.appearance.accentColor;
        result.text_color = settings.appearance.textColor;
        result.widget_background = settings.appearance.widgetBackground;
        result.card_background = settings.appearance.cardBackground;
        result.card_text_color = settings.appearance.cardTextColor;
        result.font_family = settings.appearance.fontFamily;
        result.font_size_scale = settings.appearance.fontSizeScale;
        result.widget_size = settings.appearance.widgetSize;
        result.widget_width = settings.appearance.widgetWidth;
        result.max_height = settings.appearance.maxHeight;
        result.show_greeting = settings.appearance.showGreeting;
        result.bot_avatar = settings.appearance.botAvatar;
        result.bot_name = settings.appearance.botName;
        result.show_rating = settings.appearance.showRating;
        result.show_view_product_button = settings.appearance.showViewProductButton;
        result.card_border_radius = settings.appearance.cardBorderRadius;
        result.card_shadow = settings.appearance.cardShadow;
        result.custom_css = settings.appearance.customCSS;
        result.launcher_style = settings.appearance.launcherStyle;
        result.sidebar_enabled = settings.appearance.sidebarEnabled;
        result.sidebar_side = settings.appearance.sidebarSide;
        result.sidebar_width = settings.appearance.sidebarWidth;
        if (settings.appearance.greetingMessage) {
            result.welcome_message = settings.appearance.greetingMessage;
        }
    }
    // Language settings
    if (settings.languages) {
        result.language = settings.languages.default;
        result.welcome_messages = settings.languages.welcomeMessages;
    }
    // RAG settings - pass through directly (already nested)
    if (settings.rag) {
        result.rag = settings.rag;
    }
    return result;
};
/**
 * Settings Service
 *
 * Handles all API calls for settings management
 */
class SettingsService {
    constructor() {
        Object.defineProperty(this, "baseUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "nonce", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        const apiConfig = this.getApiConfig();
        this.baseUrl = apiConfig.restUrl;
        this.nonce = apiConfig.nonce;
    }
    getApiConfig() {
        const globals = window.wooAiChatbot ?? window.wooAIChatbot;
        // Use hardcoded /wp-json to avoid string replacement issues
        return {
            restUrl: '/wp-json',
            nonce: globals?.nonce || '',
        };
    }
    async fetchAPI(endpoint, options = {}) {
        const url = `${this.baseUrl}${endpoint}`;
        const response = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': this.nonce,
                ...options.headers,
            },
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({
                message: 'Network error',
                code: 'network_error'
            }));
            throw {
                message: error.message || 'Request failed',
                code: error.code || 'unknown_error',
                data: error.data,
            };
        }
        return response.json();
    }
    /**
     * Get current settings
     */
    async getSettings() {
        const response = await this.fetchAPI('/woo-ai/v1/settings');
        const payload = response.settings ?? response.data;
        if (!response.success || !payload) {
            throw {
                message: response.message || 'Failed to load settings',
                code: 'load_failed',
            };
        }
        // Transform from PHP flat structure to frontend nested structure
        return transformFromPhp(payload);
    }
    /**
     * Update settings
     */
    async updateSettings(settings) {
        // Transform from frontend nested structure to PHP flat structure
        const phpSettings = transformToPhp(settings);
        const response = await this.fetchAPI('/woo-ai/v1/settings', {
            method: 'POST',
            body: JSON.stringify({ settings: phpSettings }),
        });
        const payload = response.settings ?? response.data;
        if (!response.success || !payload) {
            throw {
                message: response.message || 'Failed to update settings',
                code: 'update_failed',
            };
        }
        // Transform response back to frontend structure
        return transformFromPhp(payload);
    }
    /**
     * Reset settings to defaults
     */
    async resetSettings() {
        const response = await this.fetchAPI('/woo-ai/v1/settings/reset', {
            method: 'POST',
        });
        const payload = response.settings ?? response.data;
        if (!response.success || !payload) {
            throw {
                message: response.message || 'Failed to reset settings',
                code: 'reset_failed',
            };
        }
        // Transform response back to frontend structure
        return transformFromPhp(payload);
    }
    /**
     * Test AI provider connection
     */
    async testProvider(provider, _apiKey) {
        try {
            const response = await this.fetchAPI(`/woo-ai/v1/settings/test-provider/${provider}`, {
                method: 'POST',
            });
            return {
                success: response.success,
                message: response.message || 'Connection test completed',
            };
        }
        catch (error) {
            const apiError = error;
            return {
                success: false,
                message: apiError.message || 'Connection test failed',
            };
        }
    }
}
// Create singleton instance
const settingsService = new SettingsService();
/**
 * Settings Context
 */
const SettingsContext = createContext(undefined);
/**
 * Settings Provider Component
 *
 * Manages settings state with optimistic updates and error handling
 */
export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    /**
     * Load settings on mount
     */
    useEffect(() => {
        const loadSettings = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await settingsService.getSettings();
                setSettings(data);
            }
            catch (err) {
                const apiError = err;
                setError(apiError);
                console.error('Failed to load settings:', apiError);
            }
            finally {
                setLoading(false);
            }
        };
        loadSettings();
    }, []);
    /**
     * Update settings with optimistic updates
     */
    const updateSettings = useCallback(async (newSettings) => {
        if (!settings) {
            throw new Error('Settings not loaded');
        }
        // Store previous state for rollback
        const previousSettings = settings;
        try {
            // Optimistic update
            setSettings((prev) => {
                if (!prev)
                    return prev;
                return {
                    ...prev,
                    ...newSettings,
                    aiProvider: {
                        ...prev.aiProvider,
                        ...(newSettings.aiProvider || {}),
                    },
                    general: {
                        ...prev.general,
                        ...(newSettings.general || {}),
                    },
                    appearance: {
                        ...prev.appearance,
                        ...(newSettings.appearance || {}),
                    },
                    languages: {
                        ...(prev.languages || { default: 'en', enabled: ['en'], autoDetect: true }),
                        ...(newSettings.languages || {}),
                    },
                    rag: {
                        ...(prev.rag || { enabled: false, embeddingModel: 'gemini-embedding-001', chunkSize: 500, chunkOverlap: 100, topK: 5, similarityThreshold: 0.7, supabaseUrl: '', supabaseApiKey: '', supabaseTableName: 'product_embeddings', supabaseConnectionString: '', indexPages: false, indexPosts: false }),
                        ...(newSettings.rag || {}),
                    },
                };
            });
            setError(null);
            // Make API call
            const updatedSettings = await settingsService.updateSettings(newSettings);
            // Update with server response
            setSettings(updatedSettings);
        }
        catch (err) {
            const apiError = err;
            // Rollback on error
            setSettings(previousSettings);
            setError(apiError);
            // Re-throw for component-level handling
            throw apiError;
        }
    }, [settings]);
    /**
     * Reset settings to defaults
     */
    const resetSettings = useCallback(async () => {
        // Store previous state for rollback
        const previousSettings = settings;
        try {
            setLoading(true);
            setError(null);
            const defaultSettings = await settingsService.resetSettings();
            setSettings(defaultSettings);
        }
        catch (err) {
            const apiError = err;
            // Rollback on error
            if (previousSettings) {
                setSettings(previousSettings);
            }
            setError(apiError);
            // Re-throw for component-level handling
            throw apiError;
        }
        finally {
            setLoading(false);
        }
    }, [settings]);
    /**
     * Test AI provider connection
     */
    const testProvider = useCallback(async (provider) => {
        try {
            setError(null);
            // Get API key from current settings
            const apiProviderSettings = settings?.aiProvider;
            const apiKey = apiProviderSettings?.[`${provider}ApiKey`];
            return await settingsService.testProvider(provider, apiKey);
        }
        catch (err) {
            const apiError = err;
            setError(apiError);
            return {
                success: false,
                message: apiError.message || 'Connection test failed',
            };
        }
    }, [settings]);
    const value = {
        settings,
        loading,
        error,
        updateSettings,
        resetSettings,
        testProvider,
    };
    return (_jsx(SettingsContext.Provider, { value: value, children: children }));
};
/**
 * useSettings Hook
 *
 * Access settings context
 *
 * @throws {Error} If used outside SettingsProvider
 */
export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};
